import React, { createContext, useContext, useRef } from 'react';
import classNames from 'classnames';
import { excludeTypes as excludeTypesCommon } from '../util/googleMaps';
import { getPlacePredictions, getPlaceDetails, locationBounds } from '../util/googleMaps';
import { userLocation, getCurrentUserAddress } from '../util/maps';
import { setUserCurrentLocationSP } from '../util/localStorage';
import { CURRENT_LOCATION_ID } from '../util/googleMaps';
import config from '../config';
import { isLocalhost } from '../util/urlHelpers';

// When displaying data from the Google Maps Places API, and
// attribution is required next to the results.
// See: https://developers.google.com/places/web-service/policies#powered
export const GeocoderAttribution = props => {
    const { rootClassName, className } = props;
    const classes = classNames(rootClassName, className);
    return <div className={classes} />;
};

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Google Maps Places API.
 */
class GeocoderGoogleMaps {
    constructor() {
        this.sessionToken = null;
        /** Record<string, Promise<geodata>> - list of current requests, used to prevent duplicate requests */
        this.requests = {}
    }
    getNewTokenInstance() {
        const { google } = window;
        if (google && google.maps && google.maps.places) {
            return new window.google.maps.places.AutocompleteSessionToken();
        }
    }
    getSessionToken() {
        this.sessionToken = this.sessionToken || this.getNewTokenInstance();
        return this.sessionToken;
    }
    /**
     * Search places with the given name.
     *
     * @param {String} search query for place names
     *
     * @return {Promise<{ search: String, predictions: Array<Object>}>}
     * results of the geocoding, should have the original search query
     * and an array of predictions. The format of the predictions is
     * only relevant for the `getPlaceDetails` function below.
     */
    getPlacePredictions(search, searchConfigurations = {}, excludeTypes) {
        const limitCountriesMaybe = config.maps.search.countryLimit
            ? {
                componentRestrictions: {
                    country: config.maps.search.countryLimit,
                },
            }
            : {};

        return getPlacePredictions(
            search,
            this.getSessionToken(),
            {
                ...limitCountriesMaybe,
                ...searchConfigurations,
            },
            excludeTypes
        ).then(results => {
            return {
                search,
                predictions: results.predictions,
            };
        });
    }
    /**
     * Get the ID of the given prediction.
     */
    getPredictionId(prediction) {
        if (prediction.predictionPlace) {
            // default prediction defined above
            return prediction.id;
        }
        // prediction from Google Maps Places API
        return prediction.place_id;
    }
    /**
     * Get the address text of the given prediction.
     */
    getPredictionAddress(prediction) {
        if (prediction.predictionPlace) {
            // default prediction defined above
            return prediction.predictionPlace.address;
        }
        // prediction from Google Maps Places API
        return prediction.description;
    }
    /**
     * Fetch or read place details from the selected prediction.
     *
     * @param {Object} prediction selected prediction object
     *
     * @return {Promise<util.propTypes.place>} a place object
     */
    getPlaceDetails(prediction) {
        if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
            return userLocation().then(async latlng => {
                const currentAddressLocationData = await getCurrentUserAddress(latlng);
                const { addressTerms, addressComponents } = currentAddressLocationData || {};

                const { postal_code, locality } = addressTerms || { postal_code: '', locality: '' };
                const address = `${postal_code} ${locality}`;
                const origin = latlng;

                setUserCurrentLocationSP({ address, origin });

                return {
                    address,
                    address_components: addressComponents,
                    addressTerms,
                    origin,
                    bounds: locationBounds(
                        latlng,
                        prediction.applyDistance
                            ? prediction.distance
                            : config.maps.search.currentLocationBoundsDistance
                    ),
                };
            });
        }

        if (prediction.predictionPlace) {
            const { _addressSubstitution } = prediction.predictionPlace;
            if (_addressSubstitution) {
                prediction.predictionPlace.address = _addressSubstitution;
            }

            return Promise.resolve(prediction.predictionPlace);
        }

        return getPlaceDetails(prediction.place_id, this.getSessionToken(), prediction).then(
            place => {
                this.sessionToken = null;
                return place;
            }
        );
    }
    /**
     * Resolve place details from search string: first, request predictions,
     * after that get a place details from the 1st prediction data
     *
     * @param {*} locationUnitsStr string
     * @returns {Promise<util.propTypes.place | null>} place details from the
     * 1st google api autocomplete prediction
     */
    async getPlaceDetailsFromString({
        locationUnitsStr,
        excludeTypes,
        searchConfigurationsProp,
    }) {
        if (!locationUnitsStr) return null;


        if (this.requests[locationUnitsStr]) {
            if (isLocalhost()) {
                console.log('Location request already in progress');
            }
            return this.requests[locationUnitsStr].then((res) => {
                if (!res) throw new Error();
                return res
            })
                .catch(e => null)
                .finally(() => {
                    this.requests[locationUnitsStr] = null
                })
        }

        const getDetails = async () => {
            try {
                const defaultExcludeTypes = [
                    'locality',
                    'country',
                    'administrative_area_level_1',
                    'postal_code',
                    ...excludeTypesCommon,
                ]
                /** request predictions... */
                const predictionsResponse = await this.getPlacePredictions(
                    locationUnitsStr,
                    searchConfigurationsProp,
                    excludeTypes || defaultExcludeTypes,
                );
                const { predictions } = predictionsResponse

                if (!predictions || predictions.length === 0) {
                    throw new Error();
                }
                /** ...get the 1st of the predictions and request it's place details info */
                const predictionData = await this.getPlaceDetails(predictions[0]);

                if (!predictionData) {
                    throw new Error();
                }

                return predictionData;
            }
            catch (e) {
                if (isLocalhost()) {
                    console.log('getDetails.error:', e.message);
                }
                return null
            }
        }

        this.requests[locationUnitsStr] = getDetails();

        return this.requests[locationUnitsStr].then((res) => {
            if (!res) throw new Error();
            return res
        })
            .catch(e => null)
            .finally(() => {
                this.requests[locationUnitsStr] = null
            })
    }
}

export const GeocoderServiceContext = createContext(null);

export const useGeocoder = () => useContext(GeocoderServiceContext);

export const geocoderInstance = new GeocoderGoogleMaps();

export const GeocoderServiceProvider = ({ children }) => {
    const service = useRef(geocoderInstance).current;

    return (
        <GeocoderServiceContext.Provider value={service}>
            {children}
        </GeocoderServiceContext.Provider>
    );
};
